import React, { useEffect, useRef, useState } from "react";

const ProjectForm = ({ onSave, editingProject, onCancelEdit }) => {
  const [formData, setFormData] = useState({
    project_id: 0,
    project_name: "",
    project_status: "",
    project_description: "",
    project_rera: "",
    project_extent: "",
    project_location: "",
    project_badge_location: "",
    project_type: "",
    project_price: "",
    project_complete_date: "",
    approval: "",
    hmda: "",
    features: "",
    brochure: "",
    project_image: "",
    show_on_portfolio: 0,
  });

  const [preview, setPreview] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingProject) {
      const existingImg = editingProject.image || "";

      setFormData({
        project_id: editingProject.project_id || 0,
        project_name: editingProject.project_name || "",
        project_status: editingProject.project_status || "",
        project_description: editingProject.project_description || "",
        project_rera: editingProject.project_rera || "",
        project_extent: editingProject.project_extent || "",
        project_location: editingProject.project_location || "",
        project_badge_location: editingProject.project_badge_location || "",
        project_type:
          editingProject.project_type || editingProject.property_type || "",
        hmda: editingProject.hmda || "",
        project_price: editingProject.project_price || "",
        project_complete_date:
          editingProject.project_complete_date || editingProject.status_label || "",
        approval: editingProject.approval || "",
        features: editingProject.features || "",
        brochure: editingProject.brochure || "",
        image: existingImg,
        show_on_portfolio:
          editingProject.show_on_portfolio === 1 ||
            editingProject.show_on_portfolio === true
            ? 1
            : 0,
      });

      if (typeof existingImg === "string" && existingImg.trim() !== "") {
        setPreview(existingImg);
      } else {
        setPreview("");
      }
    } else {
      // RESET NEW
      setFormData({
        project_description: "",
        project_rera: "",
        project_extent: "",
        project_badge_location: "",
        hmda: "",
        brochure: "",
        project_id: 0,
        project_name: "",
        project_location: "",
        project_type: "",
        project_price: "",
        project_status: "",
        project_complete_date: "",
        approval: "",
        features: "",
        image: "",
        show_on_portfolio: 0, // ✅ RESET
      });
      setPreview("");
    }
  }, [editingProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageBoxClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData); // ✅ sends toggle value also
  };

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          {formData.project_id ? "Edit Project" : "Add Project"}
        </h2>

        {editingProject && (
          <button
            onClick={onCancelEdit}
            className="text-sm text-blue-600 hover:underline"
          >
            Cancel
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Project Name */}
        <div>
          <label className="block text-sm font-medium mb-1">Project Name*</label>
          <input
            type="text"
            name="project_name"
            className="w-full border rounded-md px-3 py-2"
            value={formData.project_name}
            onChange={handleChange}
            required
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            name="project_status"
            className="w-full border rounded-md px-3 py-2"
            value={formData.project_status}
            onChange={handleChange}
          >
            <option value="">Select status</option>
            <option value="Ongoing">Ongoing</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            name="project_description"
            className="w-full border rounded-md px-3 py-2"
            value={formData.project_description}
            onChange={handleChange}
            rows={4}
          />
        </div>

        {/* RERA + Extent */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">RERA Number</label>
            <input
              name="project_rera"
              value={formData.project_rera}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Extent</label>
            <input
              name="project_extent"
              value={formData.project_extent}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
        </div>

        {/* Location + Badge Location */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <input
              name="project_location"
              value={formData.project_location}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Badge Location</label>
            <input
              name="project_badge_location"
              value={formData.project_badge_location}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
        </div>

        {/* Additional details */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Project Type</label>
            <input
              name="project_type"
              value={formData.project_type}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Price</label>
            <input
              name="project_price"
              value={formData.project_price}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Completion Date</label>
            <input
              name="project_complete_date"
              value={formData.project_complete_date}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Approval</label>
            <input
              name="approval"
              value={formData.approval}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">HMDA</label>
            <input
              name="hmda"
              value={formData.hmda}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Features</label>
            <input
              name="features"
              value={formData.features}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
        </div>

        {/* Brochure */}
        <div>
          <label className="block text-sm font-medium mb-1">Brochure Link</label>
          <input
            name="brochure"
            value={formData.brochure}
            onChange={handleChange}
            className="w-full border rounded-md px-3 py-2"
          />
        </div>

        {/* Image */}
        <div>
          <label className="block text-sm font-medium mb-2">Project Image</label>

          {preview ? (
            <img
              src={preview}
              alt={"Project preview"}
              className="h-48 w-full object-cover rounded-lg mb-2"
            />
          ) : (
            <div
              onClick={handleImageBoxClick}
              className="border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 h-40 flex flex-col items-center justify-center cursor-pointer"
            >
              <p className="text-gray-500">Click to upload image</p>
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        {/* Toggle */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Show on website</label>
          <input
            type="checkbox"
            checked={formData.show_on_portfolio === 1}
            onChange={() =>
              setFormData((prev) => ({
                ...prev,
                show_on_portfolio: prev.show_on_portfolio === 1 ? 0 : 1,
              }))
            }
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          {formData.project_id ? "Update Project" : "Save Project"}
        </button>
      </form>

    </div>
  );
};

export default ProjectForm;
